import { useId, useState } from 'react'
import type { GccDispositionTag, GccResidualWorkload as ResidualWorkload, GccTaskSourcing, GccTaskTag } from '@shared/types'
import { GCC_DISPOSITIONS, formatSourcingPair } from '@tn/shared'
import { GCC_AGENT_ESTIMATE_LABEL } from '@shared/gcc/agent-catalog'
import { dispositionTooltip } from './dispositionTooltip'
import GccResidualWorkload, { fmtFte } from './GccResidualWorkload'
import { Badge, OWNERSHIP_STYLE, TIER_STYLE } from './GccSourcingModel'
import { BRAND_GRADIENT, GCC_BADGE } from './gccExecutiveStyle'
import type { GccBadgeStyle } from './gccExecutiveStyle'
import { QuickWinBadge, quickWinSummary } from './GccQuickWin'
import { GccTooltip, TipEnum } from './GccTooltip'
import { GCC_TOOLTIPS } from './gcc-tooltips'
import { TIER_META, OWNERSHIP_META, EXISTENCE_VERDICT_META } from './gcc-meta'

// Badge colours as in the exported GCC dashboard (WI 2487)
const DISPOSITION_STYLE: Record<GccDispositionTag, GccBadgeStyle> = {
  Eliminate: GCC_BADGE.red,
  Automate:  GCC_BADGE.blue,
  Agentify:  GCC_BADGE.purple,
  Augment:   GCC_BADGE.yellow,
  Human:     GCC_BADGE.gray,
}

const DISPOSITION_PAGE_SIZE = 20

/** The Disposition tab of the GCC dashboard: one group per AI disposition, each paginated. */
export default function DispositionTab({ taggedTasks, residual, taskSourcing, excludedEliminate, quickWinHorizonMonths }: {
  taggedTasks: GccTaskTag[]
  residual?: ResidualWorkload
  /** v2 Step 2 sourcing per task (WI 2324); v1 designs show the tier instead */
  taskSourcing?: GccTaskSourcing[]
  /** Eliminate tasks hidden by the S0b review, for the zero-Eliminate reason (WI 2576) */
  excludedEliminate: number
  /** Quick-win horizon of the run (WI 2806); absent on designs saved before it, which show no quick wins */
  quickWinHorizonMonths?: number
}) {
  const tipIdPrefix = useId()
  const [expanded, setExpanded] = useState<GccDispositionTag | null>(null)
  const [pages, setPages] = useState<Partial<Record<GccDispositionTag, number>>>({})

  const groups = GCC_DISPOSITIONS.map(tag => ({
    tag,
    tasks: taggedTasks.filter(t => t.disposition === tag),
  }))
  const total = taggedTasks.length
  const max = Math.max(...groups.map(g => g.tasks.length), 1)
  // v2 tags carry FTE (WI 2323); v1 designs keep the original columns
  const hasFte = taggedTasks.some(t => t.baselineFte !== undefined)
  const sourcingById = new Map((taskSourcing ?? []).map(s => [s.taskId, s]))
  const hasSourcing = sourcingById.size > 0

  const getPage = (tag: GccDispositionTag) => pages[tag] ?? 1
  const setPage = (tag: GccDispositionTag, p: number) => setPages(prev => ({ ...prev, [tag]: p }))

  return (
    <div>
      <GccResidualWorkload residual={residual} />
      {quickWinHorizonMonths !== undefined && (
        <section aria-label="Quick wins" className="text-xs text-gray-600 mb-2">
          <GccTooltip content={GCC_TOOLTIPS.quickWin}>
            <span>{quickWinSummary(taggedTasks.filter(t => t.quickWin).length, quickWinHorizonMonths)}</span>
          </GccTooltip>
        </section>
      )}
      <p className="text-xs text-gray-400 mb-4">
        Share of {total} tagged tasks by AI disposition — click a row to drill down
      </p>
      <div className="space-y-2">
        {groups.map(({ tag, tasks }) => {
          const pct = total > 0 ? Math.round((tasks.length / total) * 100) : 0
          const barW = total > 0 ? (tasks.length / max) * 100 : 0
          const style = DISPOSITION_STYLE[tag]
          const isOpen = expanded === tag
          const page = getPage(tag)
          const totalPages = Math.max(1, Math.ceil(tasks.length / DISPOSITION_PAGE_SIZE))
          const pageTasks = tasks.slice((page - 1) * DISPOSITION_PAGE_SIZE, page * DISPOSITION_PAGE_SIZE)
          const tip = dispositionTooltip(tag, {
            count: tasks.length, total, residualPct: residual?.residualPctByDisposition[tag], excludedEliminate,
          })
          const tipId = `${tipIdPrefix}-${tag}`

          return (
            <div key={tag} className="rounded-xl overflow-hidden border border-gray-100">
              <button
                onClick={() => setExpanded(isOpen ? null : tag)}
                aria-describedby={tipId}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors text-left"
              >
                {/* Hover shows the tooltip; focus and screen readers get the same text from aria-describedby (WI 2576) */}
                <span className="flex-shrink-0">
                  <GccTooltip iconHidden content={tip.map((line, i) => <span key={i} className={i > 0 ? 'block mt-1' : 'block'}>{line}</span>)}>
                    <Badge label={tag} style={style} className="w-20 text-center flex-shrink-0" />
                  </GccTooltip>
                </span>
                <span id={tipId} className="sr-only">{tip.join(' ')}</span>
                <div className="gx-track flex-1 h-2">
                  <div className="gx-bar h-2 rounded-full" style={{ width: `${barW}%`, background: BRAND_GRADIENT }} />
                </div>
                <span className="text-sm font-semibold text-gray-700 w-6 text-right flex-shrink-0">{tasks.length}</span>
                <span className="text-xs text-gray-400 w-8 text-right flex-shrink-0">{pct}%</span>
                <span
                  className="text-gray-400 text-xs flex-shrink-0 transition-transform duration-200"
                  style={{ transform: isOpen ? 'rotate(180deg)' : 'none' }}
                >
                  ▼
                </span>
              </button>

              {isOpen && tasks.length > 0 && (
                <div className="border-t border-gray-100 px-4 pb-4">
                  <div className="overflow-x-auto mt-3">
                    <table className="gx-tbl w-full text-xs">
                      <thead>
                        <tr className="text-left text-gray-400 border-b border-gray-50">
                          <th className="pb-2 font-medium">
                            <GccTooltip content={GCC_TOOLTIPS.dispositionTask}>Task</GccTooltip>
                          </th>
                          <th className="pb-2 font-medium">
                            {hasSourcing
                              ? <GccTooltip content={<>{GCC_TOOLTIPS.dispositionSourcing}<TipEnum entries={OWNERSHIP_META} /></>}>Sourcing</GccTooltip>
                              : <GccTooltip content={<>{GCC_TOOLTIPS.dispositionSourcingTier}<TipEnum entries={TIER_META} /></>}>Tier</GccTooltip>}
                          </th>
                          <th className="pb-2 font-medium">
                            <GccTooltip content={<>{GCC_TOOLTIPS.dispositionExistenceVerdict}<TipEnum entries={EXISTENCE_VERDICT_META} /></>}>Verdict</GccTooltip>
                          </th>
                          <th className="pb-2 font-medium">
                            <GccTooltip content={GCC_TOOLTIPS.dispositionAgentMatch}>{GCC_AGENT_ESTIMATE_LABEL}</GccTooltip>
                          </th>
                          <th className="pb-2 font-medium">Signal</th>
                          {hasFte && (
                            <th className="pb-2 font-medium text-right">
                              <GccTooltip content={GCC_TOOLTIPS.residualFte}>FTE → residual</GccTooltip>
                            </th>
                          )}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {pageTasks.map((t, i) => {
                          const src = t.taskId ? sourcingById.get(t.taskId) : undefined
                          const ts = (src ? OWNERSHIP_STYLE[src.ownership] : t.sourcingTier && TIER_STYLE[t.sourcingTier]) || GCC_BADGE.gray
                          // v2: tasks with no residual work have no Step 2 sourcing
                          const label = src ? formatSourcingPair(src.ownership, src.location) : hasSourcing ? '—' : (t.sourcingTier ?? '—')
                          return (
                            <tr key={i}>
                              <td className="py-1.5 font-medium text-gray-700 max-w-[260px]">
                                <span className="block truncate">{t.taskName}</span>
                                {t.quickWin && t.monthsToImplement !== undefined && quickWinHorizonMonths !== undefined && (
                                  <QuickWinBadge monthsToImplement={t.monthsToImplement} horizonMonths={quickWinHorizonMonths} className="mt-0.5" />
                                )}
                              </td>
                              <td className="py-1.5">
                                <span className="gx-badge" style={{ background: ts.bg, color: ts.text }}>
                                  {label}
                                </span>
                              </td>
                              <td className="py-1.5 text-gray-500">{t.existenceVerdict}</td>
                              <td className="py-1.5">
                                {t.agentMatch
                                  ? <span className="gx-t-good font-semibold">✓</span>
                                  : <span className="text-gray-300">—</span>}
                              </td>
                              <td className="py-1.5 text-gray-400">{t.signalSource}</td>
                              {hasFte && (
                                <td className="py-1.5 text-right text-gray-600 whitespace-nowrap" title={t.defaulted ? 'Not tagged by Step 1 — counted as Human' : undefined}>
                                  {fmtFte(t.baselineFte ?? 0)} → {fmtFte(t.residualFte ?? 0)}
                                  {t.defaulted && <span className="ml-1 text-amber-600">*</span>}
                                </td>
                              )}
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                  {totalPages > 1 && (
                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-50">
                      <span className="text-[10px] text-gray-400">
                        {(page - 1) * DISPOSITION_PAGE_SIZE + 1}–{Math.min(page * DISPOSITION_PAGE_SIZE, tasks.length)} of {tasks.length}
                      </span>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => setPage(tag, Math.max(1, page - 1))}
                          disabled={page === 1}
                          className="text-[10px] text-gray-500 hover:text-gray-700 disabled:opacity-30 px-2 py-0.5 rounded hover:bg-gray-100"
                        >
                          ← Prev
                        </button>
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map(n => (
                          <button
                            key={n}
                            onClick={() => setPage(tag, n)}
                            className={`w-5 h-5 text-[10px] rounded-full transition-colors ${n === page ? 'font-bold gx-page-on' : 'text-gray-400 hover:bg-gray-100'}`}
                          >
                            {n}
                          </button>
                        ))}
                        <button
                          onClick={() => setPage(tag, Math.min(totalPages, page + 1))}
                          disabled={page === totalPages}
                          className="text-[10px] text-gray-500 hover:text-gray-700 disabled:opacity-30 px-2 py-0.5 rounded hover:bg-gray-100"
                        >
                          Next →
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
